export function getEvents(inst: any) {
    return Object.getOwnPropertyNames(Object.getPrototypeOf(inst))
        .filter((k) => (k as string)?.startsWith('on'))
        .reduce((obj: any, key) => {
            obj[key] = (inst as any)[key].bind(inst);
            return obj;
        }, {});
}

export function initSVGFitToContents(element: HTMLElement): MutationObserver {
    const config = { childList: true, subtree: true };
    const observer = new MutationObserver((mutation) => {
        mutation.forEach(m => {
            Array.from(m.addedNodes)
                .filter(n => n.nodeType === 1)
                .forEach(n => {
                    if (n.nodeName === 'USE') {
                        fitToContent(n);
                    }
                    else {
                        Array.from((n as HTMLElement)?.querySelectorAll('svg > use') || [])
                            .forEach(fitToContent);
                    }
                });
        });
    });

    observer.observe(element, config);

    Array.from(element.querySelectorAll('svg > use'))
        .forEach(fitToContent);

    function fitToContent(n: any) {
        const bbox = n.getBBox();
        n.ownerSVGElement.setAttribute('width', bbox.width + 1);
        n.ownerSVGElement.setAttribute('height', bbox.height + 1);
    }

    return observer;
}

export function replace<T>(arr: T[], oldItem: T, newItem: T) {
    return [...arr.slice(0, arr.indexOf(oldItem)), newItem, ...arr.slice(arr.indexOf(oldItem) + 1)]
}


/**
 * Build process state XML for queuing a Synergy report
 * @param request Report configuration including GUID, parameters, and display options
 * @param focusKey Current focus key from context
 * @returns Complete REV_DATA_ROOT XML string ready for job queue
 */
export function buildReportXml(
    request: {
        reportGUID: string;
        reportName?: string;
        reportNumber?: string;
        outputType?: string;
        pageOrientation?: string;
        notifyEmail?: string;
        notifyAttachOutput?: boolean;
        showInactiveStudents?: boolean;
        showFooterPrintedBy?: boolean;
        maskPhoneNumbers?: boolean;
        displayPronouns?: boolean;
        parameters?: Array<{
            alias?: string;
            sourceName: string;
            sourceObject: string;
            sourceObjectGUID: string;
            elementName: string;
            value: string;
            originalValue?: string;
            conditionType?: string;
        }>;
    },
    focusKey: string
): string {
    if (!request.reportGUID) {
        throw new Error('reportGUID is required');
    }

    // Set defaults
    const reportName = request.reportName ?? 'Report';
    const reportNumber = request.reportNumber ?? 'REPORT';
    const outputType = request.outputType ?? 'PDF';
    const pageOrientation = request.pageOrientation ?? 'PORTRAIT';
    const showInactiveStudents = request.showInactiveStudents ? '1' : '0';
    const showFooterPrintedBy = request.showFooterPrintedBy !== false ? 'YES' : 'NO';
    const maskPhoneNumbers = request.maskPhoneNumbers !== false ? '1' : '0';
    const displayPronouns = request.displayPronouns ? 'Y' : 'N';
    const notifyEmail = request.notifyEmail ?? '';
    const notifyAttachment = request.notifyAttachOutput !== false ? 'Y' : '';

    const now = new Date();
    const startDate = `${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getDate().toString().padStart(2, '0')}/${now.getFullYear()}`;
    const startTime = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

    // Build REV_DATA_REQUEST elements
    let revDataRequestElements = '';

    // Add REV_VIEW
    revDataRequestElements += `<REV_VIEW GUID="${escapeXml(request.reportGUID)}" />`;

    // Add standard report metadata
    revDataRequestElements += createReportElement('Name', 'E6FC619B-D5F8-43E6-9230-4434AD1E310E', 'ReportInfo', 'Name', reportName);
    revDataRequestElements += createReportElement('Number', 'E6FC619B-D5F8-43E6-9230-4434AD1E310E', 'ReportInfo', 'Number', reportNumber);
    revDataRequestElements += createReportElement('PageOrientation', 'E6FC619B-D5F8-43E6-9230-4434AD1E310E', 'ReportInfo', 'PageOrientation', pageOrientation);
    revDataRequestElements += createReportElement('OutputType', 'E6FC619B-D5F8-43E6-9230-4434AD1E310E', 'ReportInfo', 'OutputType', outputType);
    revDataRequestElements += createReportElement('ShowInactiveStudents', 'E6FC619B-D5F8-43E6-9230-4434AD1E310E', 'ReportInfo', 'ShowInactiveStudents', showInactiveStudents);
    revDataRequestElements += createReportElement('ShowFooterPrintedBy', 'E6FC619B-D5F8-43E6-9230-4434AD1E310E', 'ReportInfo', 'ShowFooterPrintedBy', showFooterPrintedBy);
    revDataRequestElements += createReportElement('MaskPhoneNumbers', 'E6FC619B-D5F8-43E6-9230-4434AD1E310E', 'ReportInfo', 'MaskPhoneNumbers', maskPhoneNumbers);
    revDataRequestElements += createReportElement('DisplayPronouns', 'E6FC619B-D5F8-43E6-9230-4434AD1E310E', 'ReportInfo', 'DisplayPronouns', displayPronouns);

    // Add custom report parameters
    if (request.parameters) {
        for (const param of request.parameters) {
            revDataRequestElements += createReportParameter(param);
        }
    }

    // Add job queue recur settings (for one-time run)
    revDataRequestElements += createReportElement('Z_RecurType', '56D84CE5-2FDB-4F23-953B-E7C6C0E96050', 'JobQueueRecur', 'Z_RecurType', '0');
    revDataRequestElements += createReportElement('Z_StartTime', '56D84CE5-2FDB-4F23-953B-E7C6C0E96050', 'JobQueueRecur', 'Z_StartTime', startTime);
    revDataRequestElements += createReportElement('Z_StartDate', '56D84CE5-2FDB-4F23-953B-E7C6C0E96050', 'JobQueueRecur', 'Z_StartDate', startDate);
    revDataRequestElements += createReportElement('N_Email', '56D84CE5-2FDB-4F23-953B-E7C6C0E96050', 'JobQueueRecur', 'N_Email', notifyEmail);
    revDataRequestElements += createReportElement('N_Attachment', '56D84CE5-2FDB-4F23-953B-E7C6C0E96050', 'JobQueueRecur', 'N_Attachment', notifyAttachment);

    // Add Report ID identity element
    revDataRequestElements += `<REV_ELEMENT SRC_OBJECT="ReportInfo" SRC_NAME="Revelation-Reports-ReportInfo-ReportID" SRC_ELEMENT="ReportID" SRC_OBJECT_GUID="E6FC619B-D5F8-43E6-9230-4434AD1E310E">${escapeXml(request.reportGUID)}</REV_ELEMENT>`;

    // Build the complete XML structure
    return `<REV_DATA_ROOT FOCUS_KEY="${escapeXml(focusKey)}" ACTION="QUEUE_REPORT_JOB" REPORT_ID="${escapeXml(request.reportGUID)}">
  <REV_DATA_REQUEST>
    ${revDataRequestElements}
  </REV_DATA_REQUEST>
  <IDENTITY>
    <REV_ELEMENT SRC_OBJECT="ReportInfo" SRC_NAME="Revelation-Reports-ReportInfo-ReportID" SRC_ELEMENT="ReportID" SRC_OBJECT_GUID="E6FC619B-D5F8-43E6-9230-4434AD1E310E">${escapeXml(request.reportGUID)}</REV_ELEMENT>
  </IDENTITY>
  <STATE>
    <SERVER_STATE />
  </STATE>
</REV_DATA_ROOT>`;
}

/**
 * Create a report metadata element (no condition type)
 */
function createReportElement(alias: string, objectGuid: string, objectName: string, element: string, value: string): string {
    return `<REV_ELEMENT ALIAS="${escapeXml(alias)}" SRC_NAME="Revelation-Reports-${escapeXml(objectName)}-${escapeXml(element)}" SRC_OBJECT="${escapeXml(objectName)}" SRC_OBJECT_GUID="${escapeXml(objectGuid)}" SRC_ELEMENT="${escapeXml(element)}">
  <C><![CDATA[${value}]]></C>
  <O><![CDATA[${value}]]></O>
</REV_ELEMENT>`;
}

/**
 * Create a report parameter element with optional condition type
 */
function createReportParameter(param: {
    alias?: string;
    sourceName: string;
    sourceObject: string;
    sourceObjectGUID: string;
    elementName: string;
    value: string;
    originalValue?: string;
    conditionType?: string;
}): string {
    const alias = param.alias ?? param.elementName;
    const value = param.value ?? '';
    const originalValue = param.originalValue ?? '';
    const conditionTypeAttr = param.conditionType ? ` RI_CONDITION_TYPE="${escapeXml(param.conditionType)}"` : '';

    return `<REV_ELEMENT ALIAS="${escapeXml(alias)}" SRC_NAME="${escapeXml(param.sourceName)}" SRC_OBJECT="${escapeXml(param.sourceObject)}" SRC_OBJECT_GUID="${escapeXml(param.sourceObjectGUID)}" SRC_ELEMENT="${escapeXml(param.elementName)}"${conditionTypeAttr}>
  <C><![CDATA[${value}]]></C>
  <O><![CDATA[${originalValue}]]></O>
</REV_ELEMENT>`;
}

/**
 * Escape XML special characters in attribute values
 */
function escapeXml(str: string): string {
    if (!str) return '';
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}
